import * as THREE from 'three';
import Furniture from '../models/furniture.js';
import { audioCacheManager } from '../controllers/audio-cache-manager.js';

class FurnitureController {
  constructor(sceneManager, wallController) {
    this.scene = sceneManager.getScene();
    this.wallController = wallController;
    this.furnitureList = [];
    this.catalog = null;
    
    this.gridSize = 0.1;
    this.wallSnapDistance = 0.3;
    this.minimumSpacing = 0.1;
    
    this.customModelLoader = null;
  }
  
  setCustomModelLoader(loader) {
    this.customModelLoader = loader;
  }
  
  async loadCatalog() {
    try {
      const response = await fetch('/furniture-catalog.json');
      this.catalog = await response.json();
      console.log('✓ Catalogo modelli caricato:', this.catalog.categories.length, 'categorie');

      // Avvia il precaricamento di tutti i modelli 3D in background
      Furniture.preloadAll(this.catalog);

      // Avvia il precaricamento di tutti i file audio in background
      audioCacheManager.preloadFromCatalog(this.catalog);

      return this.catalog;
    } catch (error) {
      console.error('Errore caricamento catalogo:', error);
      return null;
    }
  }
  
  getCatalog() {
    return this.catalog;
  }
  
  getCategoryById(categoryId) {
    if (!this.catalog) return null;
    return this.catalog.categories.find(cat => cat.id === categoryId);
  }
  
  getItemById(itemId) {
    if (!this.catalog) return null;
    
    if (this.customModelLoader) {
      const customModels = this.customModelLoader.getCustomModels();
      const customItem = customModels.find(m => m.id === itemId);
      if (customItem) return customItem;
    }
    
    for (const category of this.catalog.categories) {
      const item = category.items.find(i => i.id === itemId);
      if (item) return item;
    }
    return null;
  }
  
  async createFurniture(catalogItemId, position) {
    const catalogItem = this.getItemById(catalogItemId);
    if (!catalogItem) {
      console.error('Item non trovato nel catalogo:', catalogItemId);
      return null;
    }
    
    const furniture = new Furniture(catalogItem, position);
    
    try {
      await furniture.load();
      
      if (this.checkCollision(furniture, position)) {
        console.warn('Impossibile posizionare: spazio insufficiente (minimo 10cm)');
        furniture.dispose();
        return null;
      }
      
      this.scene.add(furniture.mesh);
      this.furnitureList.push(furniture);
      
      console.log('✓ Modello aggiunto:', furniture.catalogItem.name);
      return furniture;
    } catch (error) {
      console.error('Errore creazione modello:', error);
      return null;
    }
  }
  
  removeFurniture(furnitureId) {
    const index = this.furnitureList.findIndex(f => f.id === furnitureId);
    if (index === -1) return false;
    
    const furniture = this.furnitureList[index];
    this.scene.remove(furniture.mesh);
    furniture.dispose();
    this.furnitureList.splice(index, 1);
    
    console.log('✓ Modello rimosso:', furniture.catalogItem.name);
    return true;
  }
  
  getFurnitureById(id) {
    return this.furnitureList.find(f => f.id === id);
  }
  
  getAllFurniture() {
    return this.furnitureList;
  }
  
  moveFurniture(furnitureId, newPosition, applySnap = true) {
    const furniture = this.getFurnitureById(furnitureId);
    if (!furniture) return false;
    
    let finalPosition = newPosition.clone();
    
    if (applySnap) {
      if (furniture.catalogItem.snapToGrid) {
        finalPosition = this.snapToGrid(finalPosition);
      }
      if (furniture.catalogItem.snapToWall) {
        const snapped = this.snapToWall(finalPosition, furniture);
        if (snapped) finalPosition = snapped;
      }
    }
    
    if (this.checkCollision(furniture, finalPosition)) {
      console.warn('Movimento bloccato: spazio insufficiente (minimo 10cm tra oggetti)');
      return false;
    }
    
    furniture.setPosition(finalPosition.x, finalPosition.y, finalPosition.z);
    return true;
  }
  
  rotateFurniture(furnitureId, degrees) {
    const furniture = this.getFurnitureById(furnitureId);
    if (!furniture) return false;
    furniture.rotate(degrees);
    return true;
  }
  
  async duplicateFurniture(furnitureId) {
    const original = this.getFurnitureById(furnitureId);
    if (!original) {
      console.warn('Modello originale non trovato per duplicazione');
      return null;
    }
    
    const dims = original.catalogItem.dimensions;
    const maxDimension = Math.max(dims.width, dims.depth);
    const offset = maxDimension + this.minimumSpacing * 2;
    
    console.log(`Duplicazione ${original.catalogItem.name}: offset calcolato = ${offset.toFixed(2)}m`);
    
    const positions = [
      new THREE.Vector3(original.position.x + offset,        original.position.y, original.position.z + offset),
      new THREE.Vector3(original.position.x + offset,        original.position.y, original.position.z),
      new THREE.Vector3(original.position.x,                 original.position.y, original.position.z + offset),
      new THREE.Vector3(original.position.x + offset * 1.5,  original.position.y, original.position.z + offset * 1.5),
      new THREE.Vector3(original.position.x - offset,        original.position.y, original.position.z + offset),
      new THREE.Vector3(original.position.x + offset,        original.position.y, original.position.z - offset)
    ];
    
    for (let i = 0; i < positions.length; i++) {
      const clonePosition = positions[i];
      const cloned = new Furniture(original.catalogItem, clonePosition);
      cloned.rotation = original.rotation;
      cloned.scale    = original.scale;
      
      try {
        await cloned.load();
        cloned.updateBoundingBox();
        
        if (!this.checkCollision(cloned, clonePosition)) {
          this.scene.add(cloned.mesh);
          this.furnitureList.push(cloned);
          console.log(`✓ Modello duplicato: ${cloned.catalogItem.name} (tentativo ${i + 1})`);
          return cloned;
        } else {
          console.log(`Tentativo ${i + 1}: collisione rilevata, provo posizione alternativa...`);
          cloned.dispose();
        }
      } catch (error) {
        console.error('Errore durante duplicazione:', error);
        cloned.dispose();
        return null;
      }
    }
    
    console.warn('Impossibile duplicare: nessuna posizione valida trovata dopo tutti i tentativi');
    return null;
  }
  
  snapToGrid(position) {
    return new THREE.Vector3(
      Math.round(position.x / this.gridSize) * this.gridSize,
      position.y,
      Math.round(position.z / this.gridSize) * this.gridSize
    );
  }
  
  snapToWall(position, furniture) {
    const walls = this.wallController.getAllWalls();
    if (walls.length === 0) return null;
    
    let closestWall = null;
    let minDistance = Infinity;
    let closestPoint = null;
    
    walls.forEach(wall => {
      const result = this.getClosestPointOnWall(position, wall);
      if (result.distance < minDistance && result.distance < this.wallSnapDistance) {
        minDistance  = result.distance;
        closestWall  = wall;
        closestPoint = result.point;
      }
    });
    
    if (closestWall && closestPoint) {
      const wallAngle = closestWall.getAngle();
      const normal = new THREE.Vector3(-Math.sin(wallAngle), 0, Math.cos(wallAngle));
      const furnitureDepth = furniture.catalogItem.dimensions.depth / 2;
      const offset = normal.multiplyScalar(furnitureDepth + closestWall.thickness / 2);
      return closestPoint.clone().add(offset);
    }
    
    return null;
  }
  
  getClosestPointOnWall(point, wall) {
    const start = wall.start.clone();
    const end   = wall.end.clone();
    const p     = new THREE.Vector3(point.x, 0, point.z);
    
    const lineVec = new THREE.Vector3().subVectors(end, start);
    const lineLen = lineVec.length();
    lineVec.normalize();
    
    const pointVec = new THREE.Vector3().subVectors(p, start);
    const t = pointVec.dot(lineVec);
    
    let closestPoint;
    if (t <= 0)       closestPoint = start;
    else if (t >= lineLen) closestPoint = end;
    else              closestPoint = start.clone().add(lineVec.multiplyScalar(t));
    
    return { point: closestPoint, distance: p.distanceTo(closestPoint) };
  }
  
  checkCollision(furniture, newPosition) {
    if (!furniture.boundingBox) {
      console.warn('Bounding box non disponibile per la verifica collisioni');
      return false;
    }
    
    const tempBox = new THREE.Box3();
    const dims    = furniture.catalogItem.dimensions;
    const margin  = this.minimumSpacing;
    
    tempBox.min.set(
      newPosition.x - (dims.width  / 2) - margin,
      newPosition.y - margin,
      newPosition.z - (dims.depth  / 2) - margin
    );
    tempBox.max.set(
      newPosition.x + (dims.width  / 2) + margin,
      newPosition.y + dims.height + margin,
      newPosition.z + (dims.depth  / 2) + margin
    );
    
    for (const other of this.furnitureList) {
      if (other.id === furniture.id) continue;
      const otherBox = other.getBoundingBox();
      if (otherBox && tempBox.intersectsBox(otherBox)) return true;
    }
    
    return false;
  }
  
  selectAll()    { this.furnitureList.forEach(f => f.select()); }
  deselectAll()  { this.furnitureList.forEach(f => f.deselect()); }
  
  deleteSelected() {
    const selected = this.furnitureList.filter(f => f.isSelected);
    selected.forEach(f => this.removeFurniture(f.id));
    return selected.length;
  }
  
  serialize() {
    return this.furnitureList.map(f => f.serialize());
  }
  
  async deserialize(data) {
    this.furnitureList.forEach(f => {
      this.scene.remove(f.mesh);
      f.dispose();
    });
    this.furnitureList = [];
    
    for (const itemData of data) {
      const catalogItem = this.getItemById(itemData.catalogItemId);
      if (catalogItem) {
        const furniture = Furniture.deserialize(itemData, catalogItem);
        await furniture.load();
        this.scene.add(furniture.mesh);
        this.furnitureList.push(furniture);
      }
    }
  }
  
  dispose() {
    this.furnitureList.forEach(furniture => {
      this.scene.remove(furniture.mesh);
      furniture.dispose();
    });
    this.furnitureList = [];
  }
}

export default FurnitureController;